import {
  Breadcrumbs,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  colors,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Link,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { makeStyles } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import _ from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useAlert } from "react-alert";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useInterval from "react-use/lib/useInterval";

import { fetchSources } from "../actions/sources";
import { RefreshIcon } from "../components/icons/themes/windows10";
import {
  ConfigureIntegrationsModal,
  SOURCE_INTEGRATION,
} from "../destinations/modals/ConfigureIntegrationModal";
import { useModalStyles } from "../destinations/modals/styles";
import Label from "../Label";
import Navigation from "../Navigation";
import { SET_SINGLE_SOURCE_CONFIG } from "../reducers/sources";
import { makeRequest } from "../util";
import { ConfigurableSourcePages } from "./sources";

const useStyles = makeStyles((theme) => ({
  navigation: {
    paddingTop: 12,
  },
  card: {
    width: "50%",
    minWidth: 800,
    minHeight: 600,
  },
  settingsTitle: {
    marginTop: 40,
    marginLeft: 30,
    marginBottom: 20,
  },
  cardContent: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  body: {
    margin: 24,
  },
  headerAction: {
    marginTop: 0,
  },
  headerIcon: {
    marginRight: theme.spacing(2),
  },
  configTextDisabled: {
    color: "#CCC",
  },
  titleWrapper: {
    display: "flex",
    alignItems: "center",
  },
  lightText: {
    color: "#546E7A",
  },
  darkText: {
    color: "#263238",
  },
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
  configRowDisabled: {
    background: "#FAFAFA",
  },
  totallyInvalidWarning: {
    color: colors.red[600],
    fontSize: "14px",
  },
  partiallyInvalidWarning: {
    color: colors.orange[600],
    fontSize: "14px",
  },
  label: {
    width: 120,
  },
  scheduleMenu: {
    width: 120,
  },
  formControl: {
    height: 500,
    overflow: "auto",
  },
  centerElements: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
}));

function SourceConfigRow({ sourceInfo, onOpenConfigModal }) {
  const classes = useStyles();

  const isConfigured = !!sourceInfo;
  const labelColor = isConfigured ? colors.green[600] : colors.red[600];
  const labelText = isConfigured ? "Configured" : "Not configured";

  return (
    <TableRow>
      <TableCell>
        <h5 className={clsx("text-md font-medium text-brand-navy")}>
          Configuration
        </h5>
      </TableCell>
      <TableCell align="center">
        {/* We add an icon here to align all the columns since we use a loading 
            icon in one of the other rows. */}
        <RefreshIcon className={clsx("mr-2", "invisible")} />
        {labelText && (
          <Label color={labelColor} className={clsx(classes.label)}>
            {labelText}
          </Label>
        )}
      </TableCell>
      <TableCell align="right">
        <Button
          onClick={() => onOpenConfigModal()}
          color="primary"
          variant="outlined"
        >
          Configure
        </Button>
      </TableCell>
    </TableRow>
  );
}

function SyncTablesRow({ name, sourceInfo }) {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);

  const isConfigured = !!sourceInfo;
  const disabled = !isConfigured;
  const numTables = sourceInfo ? sourceInfo.num_configured_tables : 0;

  var color;
  if (!sourceInfo) {
    color = colors.grey[400];
  } else if (numTables === 0) {
    color = colors.red[400];
  } else {
    color = colors.green[600];
  }

  const label = (
    <Label className={classes.label} color={color}>
      {numTables} Table
      {numTables !== 1 && "s"} Configured
    </Label>
  );

  return (
    <>
      {modalOpen && (
        <ConfigureTablesModal name={name} onClose={() => setModalOpen(false)} />
      )}
      <TableRow className={clsx(!isConfigured && classes.configRowDisabled)}>
        <TableCell>
          <h5
            className={clsx(
              "text-md font-medium text-brand-navy",
              disabled && classes.configTextDisabled
            )}
          >
            Tables
          </h5>
        </TableCell>
        <TableCell align="center">
          <RefreshIcon className={clsx("mr-2", "invisible")} />
          {label}
        </TableCell>
        <TableCell align="right">
          <Button
            disabled={disabled}
            onClick={() => setModalOpen(true)}
            color="primary"
            variant="outlined"
          >
            Configure
          </Button>
        </TableCell>
      </TableRow>
    </>
  );
}

var TableCheckbox = function TableCheckbox({ tableName, value, onChange }) {
  return (
    <FormControlLabel
      control={
        <Checkbox
          className="m-1"
          checked={value}
          onChange={(e) => onChange(tableName, e.target.checked)}
          name={tableName}
        />
      }
      label={tableName}
    />
  );
};
TableCheckbox = React.memo(TableCheckbox);

function ConfigureTablesModal({ name, onClose }) {
  const classes = useModalStyles();
  const configClasses = useStyles();
  const alert = useAlert();
  const dispatch = useDispatch();

  const [saving, setSaving] = useState(false);
  const [tables, setTables] = useState(null);

  useEffect(() => {
    makeRequest("get_tables_for_source", { source_name: name })
      .then((result) => {
        setTables(result.data);
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to fetch tables.");
        console.log(error);
        Sentry.captureException(error);
      });
  }, [alert, name]);

  function handleSave() {
    if (saving) {
      return;
    }
    setSaving(true);
    makeRequest("configure_tables_for_source", {
      source_name: name,
      tables: tables,
    })
      .then(() => {
        alert.success("Configured Selected Tables");
        dispatch(fetchSources());
        onClose();
        setSaving(false);
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to configure tables.");
        console.log(error);
        Sentry.captureException(error);
        setSaving(false);
      });
  }

  const handleToggleTable = useCallback((tableName, value) => {
    setTables((tables) => {
      return _.extend({}, tables, { [tableName]: value });
    });
  }, []);

  return (
    <Modal onClose={onClose} open={true}>
      <Card className={clsx(classes.root, classes.smallModal)}>
        <CardHeader
          title={`${name} Tables`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <div className="mx-12">
          <FormControl
            component="fieldset"
            className={clsx(
              "w-full",
              "overflow-auto",
              classes.formControl,
              configClasses.formControl,
              !tables && configClasses.centerElements
            )}
          >
            {!tables && (
              <>
                <Typography className={clsx(configClasses.darkText)}>
                  Fetching Available Tables&nbsp;
                </Typography>
                <RefreshIcon className={clsx("animate-spin mr-2")} />
              </>
            )}
            <FormGroup>
              {tables &&
                _(tables)
                  .toPairs()
                  .map(([tableName, value]) => {
                    return (
                      <TableCheckbox
                        key={tableName}
                        tableName={tableName}
                        value={value}
                        onChange={handleToggleTable}
                      />
                    );
                  })
                  .value()}
            </FormGroup>
          </FormControl>
        </div>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={onClose}>Close</Button>
          <Button color="primary" onClick={handleSave} variant="contained">
            Save
          </Button>
          <RefreshIcon
            className={clsx("animate-spin mr-2", !saving && "invisible")}
          />
        </CardActions>
      </Card>
    </Modal>
  );
}

function SyncScheduleRow({ name, sourceInfo }) {
  const classes = useStyles();
  const alert = useAlert();
  const dispatch = useDispatch();
  const [menuSchedule, setMenuSchedule] = useState("24 hours");

  const existingSchedule = sourceInfo && sourceInfo.schedule;
  const isConfigured = !!sourceInfo;
  const disabled = !isConfigured;

  useEffect(() => {
    if (existingSchedule) {
      setMenuSchedule(existingSchedule);
    }
  }, [existingSchedule]);

  function handleSaveSchedule() {
    const [count, unit] = menuSchedule.split(" ");
    makeRequest("set_source_schedule", {
      source_name: name,
      count: +count,
      unit_type: unit,
    })
      .then(() => {
        dispatch(fetchSources());
        alert.success("Schedule Saved");
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable schedule sync.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  return (
    <TableRow className={clsx(!isConfigured && classes.configRowDisabled)}>
      <TableCell>
        <h5
          className={clsx(
            "text-md font-medium text-brand-navy",
            disabled && classes.configTextDisabled
          )}
        >
          Schedule
        </h5>
      </TableCell>
      <TableCell align="center">
        <RefreshIcon className={clsx("mr-2 invisible")} />
        <div className={clsx(classes.scheduleMenu, "mx-auto inline-block")}>
          <TextField
            fullWidth
            label="Frequency"
            name="sync-frequency"
            onChange={(e) => setMenuSchedule(e.target.value)}
            select
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true }}
            value={menuSchedule}
            variant="outlined"
          >
            <option value="15 minutes">15 Minutes</option>
            <option value="1 hours">1 Hour</option>
            <option value="6 hours">6 Hours</option>
            <option value="24 hours">24 Hours</option>
          </TextField>
        </div>
      </TableCell>
      <TableCell align="right">
        <Button
          disabled={disabled || existingSchedule === menuSchedule}
          onClick={() => handleSaveSchedule()}
          color="primary"
          variant="outlined"
        >
          Save Schedule
        </Button>
      </TableCell>
    </TableRow>
  );
}

function SyncStatusRow({ name, sourceInfo }) {
  const alert = useAlert();
  const dispatch = useDispatch();
  const classes = useStyles();
  const isRefreshing = useSelector((state) => state.sources.refreshing);

  const isConfigured = !!sourceInfo;
  const syncInProgress = sourceInfo && sourceInfo.sync_in_progress;

  useInterval(() => {
    if (!isRefreshing) {
      dispatch(fetchSources());
    }
  }, 15000);

  function triggerSync() {
    makeRequest("trigger_source_sync", { source_name: name })
      .then(() => {
        dispatch({
          type: SET_SINGLE_SOURCE_CONFIG,
          payload: {
            [sourceInfo.name]: _.extend({}, sourceInfo, {
              sync_in_progress: true,
            }),
          },
        });
        dispatch(fetchSources());
        alert.success("Sync Started");
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to trigger sync.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  const disabled = !isConfigured;

  const label = (
    <Label
      className={classes.label}
      color={disabled ? colors.grey[400] : colors.green[600]}
    >
      {syncInProgress ? "Sync In Progress" : "No Sync In Progress"}
    </Label>
  );

  return (
    <TableRow className={clsx(!isConfigured && classes.configRowDisabled)}>
      <TableCell>
        <h5
          className={clsx(
            "text-md font-medium text-brand-navy",
            disabled && classes.configTextDisabled
          )}
        >
          Status
        </h5>
      </TableCell>
      <TableCell align="center">
        <RefreshIcon
          className={clsx("animate-spin mr-2", !syncInProgress && "invisible")}
        />
        {label}
      </TableCell>
      <TableCell align="right">
        <Button
          disabled={disabled || syncInProgress}
          onClick={() => triggerSync()}
          color="primary"
          variant="outlined"
        >
          Trigger Sync Now
        </Button>
      </TableCell>
    </TableRow>
  );
}

function SourceConfigTableBody({ source, sourceInfo, onOpenConfigModal }) {
  return (
    <TableBody>
      <SourceConfigRow
        name={source.name()}
        onOpenConfigModal={onOpenConfigModal}
        sourceInfo={sourceInfo}
      />
      <SyncTablesRow name={source.name()} sourceInfo={sourceInfo} />
      <SyncScheduleRow name={source.name()} sourceInfo={sourceInfo} />
      <SyncStatusRow name={source.name()} sourceInfo={sourceInfo} />
    </TableBody>
  );
}

function SourceConfigPage({ source }) {
  const classes = useStyles();
  const history = useHistory();
  const [configModalOpen, setConfigModalOpen] = useState(false);

  const loading = useSelector((state) => state.sources.loading);
  const configuredSource = useSelector(
    (state) => state.sources.data[source.name()]
  );

  const SourceIcon = source.logoSquare();
  const title = `${source.name()}: Configuration Settings`;

  const lastSyncStatus = configuredSource && configuredSource.last_sync;
  var lastSyncDescription = null;
  if (lastSyncStatus) {
    const timeSince = moment.duration(
      moment().diff(moment.unix(lastSyncStatus.last_sync_time))
    );

    if (lastSyncStatus.last_sync_failed) {
      lastSyncDescription = (
        <span className="text-red-600">
          Last sync failed {timeSince.humanize()} ago.{" "}
          {lastSyncStatus.failure_reason}.
        </span>
      );
    } else {
      lastSyncDescription = (
        <span className="text-green-600">
          Last sync succeeded {timeSince.humanize()} ago.
        </span>
      );
    }
  }

  return (
    <>
      <Card className={clsx(classes.card)}>
        <CardHeader
          title={
            <div className={classes.titleWrapper}>
              <SourceIcon
                className={clsx(classes.headerIcon)}
                height={24}
                width={24}
              />
              {title}
            </div>
          }
          action={
            <Button variant="outlined" onClick={() => history.goBack()}>
              Back
            </Button>
          }
        />

        <Divider />
        {!loading && (
          <CardContent className={clsx(classes.cardContent)}>
            <div className={clsx(classes.body)}>{source.configBodyHelp()}</div>
            <div className={clsx(classes.body)}>
              <Typography variant="body2">{lastSyncDescription}</Typography>
            </div>
            <Divider />
            <Table>
              <SourceConfigTableBody
                sourceInfo={configuredSource}
                source={source}
                onOpenConfigModal={() => setConfigModalOpen(true)}
              />
            </Table>
          </CardContent>
        )}
      </Card>
      {configModalOpen && (
        <ConfigureIntegrationsModal
          includeHeader={true}
          open={configModalOpen}
          onClose={() => setConfigModalOpen(false)}
          onSave={() => {}}
          integration={source.info}
          integrationType={SOURCE_INTEGRATION}
          settings={configuredSource ? configuredSource.settings : {}}
          existingConfig={configuredSource}
        />
      )}
    </>
  );
}

const SourcesConfigPageWrapper = ({ sourceSlug }) => {
  const history = useHistory();
  const classes = useStyles();

  const source = ConfigurableSourcePages[sourceSlug];

  return (
    <Navigation className={clsx(classes.navigation)}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breacrumb"
        className={clsx(classes.breadcrumb)}
      >
        <Link color="inherit" onClick={() => history.push(`/sources`)}>
          <Typography className={clsx(classes.lightText)} variant="overline">
            Sources
          </Typography>
        </Link>
        <Typography className={clsx(classes.darkText)} variant="overline">
          {source.name().toUpperCase()}
        </Typography>
      </Breadcrumbs>
      <SourceConfigPage source={source} />
    </Navigation>
  );
};

export default SourcesConfigPageWrapper;
